import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import {
	Spin,
	Divider,
	Drawer,
	Input,
	Button,
	Select,
	InputNumber,
	Switch,
	Tooltip,
	Alert,
} from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRYARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faBuildingShield,
	faServer,
	faCircleInfo,
	faPlug,
	faGear,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
	updateOpenModalCreateNewBank,
	updateBankAlert,
} from "../../../redux/banks/bankSlice";
import { usePostBankMutation } from "../../../redux/banks/bankAPI";
import { useLazyGetServersByOrganizationQuery } from "../../../redux/servers/serverAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// COMPONENTS
import OrganizationSelectOrganizationSubOrganization from "../../organizationManagement/organizations/OrganizationSelectOrganizationSubOrganization";
import ConfigurationFilesAssignConfiguration from "../../configurationManagement/configurationFiles/ConfigurationFilesAssignConfiguration";
import AdaptersAssignAdapter from "../adapters/AdaptersAssignAdapter";
// STYLES

function BanksCreateForm() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Option } = Select;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [listServers, setListServers] = useState<any[]>([]);
	const [numberOfColumns, setNumberOfColumns] = useState(1);
	const [organizationsAndChildsIsLoading, setOrganizationsAndChildsIsLoading] =
		useState(false);
	const [active, setActive] = useState(true);
	const [name, setName] = useState("");
	const [liftType, setLiftType] = useState("ELEVATOR");
	const [rearDoors, setRearDoors] = useState(true);
	const [board, setBoard] = useState(0);
	const [logicalPort, setLogicalPort] = useState(0);
	const [server, setServer] = useState("");
	const [channels, setChannels] = useState<any>([]);
	const [channel, setChannel] = useState("");
	const [channelType, setChannelType] = useState("");
	const [dataBlocks, setDataBlocks] = useState([]);
	const [floorsAndDirectionData, setFloorsAndDirectionDAta] = useState({
		numberOfStops: 0,
		numberOfFloorsBelowLobby: 0,
		numberOfLifts: 1,
		configurationId: "",
		adapterId: "",
	});
	const [isLoading, setIsLoading] = useState(false);
	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { openModalCreateNewBank } = useSelector((state: any) => state.bank);
	const { userLogged, theme } = useSelector((state: any) => state.home);
	const { organizationAndChilds } = useSelector(
		(state: any) => state.organization
	);
	const {
		idServerSelected,
		nameServerSelected,
		server: serverFromServerTable,
	} = useSelector((state: any) => state.server);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		postBank,
		{
			isSuccess: isSuccessPostBank,
			isLoading: isLoadingPostBank,
			isError: isErrorPostBank,
			error: errorPostBank,
			reset: resetPostBank,
		},
	] = usePostBankMutation();
	const [
		triggerGetServersByOrganization,
		{ data: dataGetServers, isLoading: isLoadingGetServers },
	] = useLazyGetServersByOrganizationQuery();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const showIsLoading = () => {
		setIsLoading(true);
		setTimeout(() => {
			setIsLoading(false);
		}, 1000);
	};

	const cleanData = () => {
		setListServers([]);
		setNumberOfColumns(1);
		setLogicalPort(0);
		setRearDoors(true);
		setLiftType("ELEVATOR");
		setName("");
		setActive(true);
		setBoard(0);
		setChannel("");
		setChannelType("");
		setFloorsAndDirectionDAta({
			numberOfStops: 0,
			numberOfFloorsBelowLobby: 0,
			numberOfLifts: 1,
			configurationId: "",
			adapterId: "",
		});
	};

	const onChangeName = (newValue: string) => {
		setName(newValue);
	};

	const onChangeType = (newValue: string) => {
		showIsLoading();
		setLiftType(newValue);
	};

	const onChangeActive = (newValue: boolean) => {
		setActive(newValue);
	};

	const onChangeBoard = (newValue: number) => {
		setBoard(newValue);
	};

	const onChangeServer = (newValue: string) => {
		setServer(newValue);
		setChannelType("");
		setChannel("");
		const index = listServers.findIndex(
			(object: any) => object.id === newValue
		);
		setChannels(listServers[index].channels);
	};

	const onChangeChannel = (newValue: string) => {
		const index = channels.findIndex((object: any) => object.id === newValue);
		if (index >= 0) {
			setChannelType(channels[index].type);
		}
		setChannel(newValue);
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		// POST body
		const postBody: any = {
			// configuration data
			floor_count: floorsAndDirectionData.numberOfStops,
			floor_below_lobby: floorsAndDirectionData.numberOfFloorsBelowLobby,
			stops: floorsAndDirectionData.numberOfStops,
			number_of_lifts: floorsAndDirectionData.numberOfLifts,
			configuration_id: floorsAndDirectionData.configurationId,
			// Addapters
			adapter_id: floorsAndDirectionData.adapterId,
			// bank data
			active,
			lifts: [],
			name,
			server_id: server,
			type: liftType,
			columns: numberOfColumns,
			board,
			logical_port: logicalPort,
			rear_doors: liftType === "ELEVATOR" ? rearDoors : false,
			offsets: [],
			floor_names: [],
			direct_link: "",
			channel_id: channel,
			configurations: [floorsAndDirectionData.configurationId],
			data_blocks: dataBlocks,
			control_panel: JSON.stringify({
				carcall: {
					code: "cc",
					map: [
						{
							offset: "3c",
							target_bit: "01",
							new_bit: "10",
						},
					],
				},
				rear_carcall: {
					code: "rcc",
					map: [
						{
							offset: "3c",
							new_byte: "1a",
						},
					],
				},
				close_door: {
					code: "cd",
					map: [
						{
							offset: "3c",
							target_bit: "01",
							new_bit: "10",
						},
					],
				},
				open_door: {
					code: "od",
					map: [
						{
							offset: "3c",
							new_byte: "1a",
						},
					],
				},
				hall_call: {
					code: "hc",
					map: [
						{
							offset: "3c",
							target_bit: "01",
							new_bit: "10",
						},
					],
				},
				secure: {
					code: "sec",
					map: [
						{
							offset: "3c",
							new_byte: "10",
						},
					],
				},
			}),
		};
		if (
			channelType === "SERIAL" ||
			channelType === "TCP" ||
			channelType === "TCP_BOT"
		) {
			delete postBody.adapter_id;
		}
		if (channelType === "ADAPTER") {
			delete postBody.configuration_id;
		}
		postBank({ token, body: postBody });
	};

	const onClickClose = () => {
		cleanData();
		dispatch(updateOpenModalCreateNewBank(false));
	};

	const launchGetServers = async (value: string) => {
		const token = await GETJwtToken();
		let orgId = userLogged.org_id;
		if (value !== "") {
			orgId = value;
		}
		triggerGetServersByOrganization({ token, orgId });
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (openModalCreateNewBank) {
			showIsLoading();
			resetPostBank();
		}
	}, [openModalCreateNewBank]);

	useEffect(() => {
		if (isSuccessPostBank) {
			setTimeout(() => {
				dispatch(
					updateBankAlert({
						title: t("general.success"),
						description: t("edgeManagement.banks.bankCreated"),
						status: "success",
					})
				);
			}, 150);
			onClickClose();
		}
		if (isErrorPostBank) {
			setTimeout(() => {
				dispatch(
					updateBankAlert({
						title: t("general.error"),
						description: errorPostBank,
						status: "error",
					})
				);
			}, 150);
			onClickClose();
		}
	}, [isSuccessPostBank, isErrorPostBank]);

	useEffect(() => {
		if (
			dataGetServers &&
			dataGetServers.data &&
			dataGetServers.data.length > 0
		) {
			setListServers(dataGetServers.data);
		} else {
			setListServers([]);
			if (idServerSelected === "") {
				setServer("");
			}
		}
	}, [dataGetServers]);

	useEffect(() => {
		if (
			organizationAndChilds &&
			organizationAndChilds.data &&
			organizationAndChilds.data.idOrganizationSelected
		) {
			setListServers([]);
			launchGetServers(organizationAndChilds.data.idOrganizationSelected);
		}
	}, [organizationAndChilds]);

	useEffect(() => {
		if (idServerSelected && idServerSelected !== "") {
			setServer(idServerSelected);
			if (
				serverFromServerTable &&
				serverFromServerTable.channels &&
				serverFromServerTable.channels.length > 0
			) {
				setChannels(serverFromServerTable.channels);
			}
		}
	}, [idServerSelected]);

	useEffect(() => {
		showIsLoading();
		setFloorsAndDirectionDAta({
			numberOfStops: 0,
			numberOfFloorsBelowLobby: 0,
			numberOfLifts: 1,
			configurationId: "",
			adapterId: "",
		});
	}, [channelType]);
	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Drawer
			width='35%'
			placement='right'
			onClose={onClickClose}
			closable={false}
			open={openModalCreateNewBank}
		>
			<Spin
				spinning={
					isLoadingPostBank ||
					isLoadingGetServers ||
					organizationsAndChildsIsLoading ||
					isLoading
				}
				tip={t("general.loading")}
			>
				{/** ---------------------------------------------------- */}
				{/** BANK INFORMATION */}
				<div>
					<Divider orientation='left' className='generalStyles__drawerDivider'>
						<h5>
							<FontAwesomeIcon
								icon={faBuildingShield}
								className='generalStyles__info generalStyles__mrFix'
							/>
							{t("edgeManagement.banks.createNewBank")}
						</h5>
					</Divider>
				</div>
				<div className='container'>
					<div className={`drawer__box__${theme}`}>
						{/** NAME */}
						<div className='col-md-12'>
							<div className='generalStyles__flex'>
								<div className='generalStyles__inputFlexName'>
									<span className='generalStyles__inputFlexRequired'>*</span>
									<span className='drawer__text'>
										{t("edgeManagement.banks.bankName")}
									</span>
								</div>
								<div className='generalStyles__width100'>
									<Input
										className=''
										size='small'
										onChange={(value) => {
											onChangeName(value.target.value);
										}}
										value={name}
									/>
								</div>
								<div className='generalStyles__infoTooltipIconBlank' />
							</div>
						</div>
						{/** LIFT TYPE */}
						<div className='col-md-12 mt-3'>
							<div className='generalStyles__flex'>
								<div className='generalStyles__inputFlexName'>
									<span className='generalStyles__inputFlexRequired'>*</span>
									<span>{t("edgeManagement.lifts.type")}</span>
								</div>
								<div className='generalStyles__width100'>
									<Select
										value={liftType}
										onChange={onChangeType}
										className='generalStyles__width100'
										size='small'
									>
										{["ELEVATOR", "ESCALATOR", "MOVINGWALK", "BOT"].map(
											(item: any) => (
												<Option key={item} value={item}>
													{item}
												</Option>
											)
										)}
									</Select>
								</div>
								<div className='generalStyles__infoTooltipIconBlank' />
							</div>
						</div>
						{/** BOARD */}
						<div className='col-md-12 mt-3'>
							<div className='generalStyles__flex'>
								<div className='generalStyles__inputFlexName'>
									<span>{t("configuration.configurationFiles.board")}</span>
								</div>
								<div className='generalStyles__width100'>
									<InputNumber
										className='generalStyles__inputNumberFull'
										precision={0}
										value={board}
										onChange={(e: any) => {
											onChangeBoard(e);
										}}
										max={99999}
										size='small'
									/>
								</div>
								<div className='generalStyles__infoTooltipIconBlank' />
							</div>
						</div>
						{/** BANK ACTIVE */}
						<div className='col-md-12 mt-3'>
							<div className='generalStyles__flex'>
								<div className='generalStyles__inputFlexName'>
									<span>{t("general.active")}</span>
								</div>
								<div className='generalStyles__width100'>
									<Switch onChange={onChangeActive} checked={active} />
								</div>
								<div className='generalStyles__infoTooltipIconBlank' />
							</div>
						</div>
					</div>
					{idServerSelected === "" ? (
						<>
							{/** ---------------------------------------------------- */}
							{/** ASSIGN TO ORGANIZATION -- */}
							<OrganizationSelectOrganizationSubOrganization
								setOrganizationsAndChildsIsLoading={
									setOrganizationsAndChildsIsLoading
								}
							/>
							<br />
							{/** ---------------------------------------------------- */}
							{/** SERVERS -- */}
							<Divider
								orientation='left'
								className='generalStyles__drawerDivider'
							>
								<h5>
									<FontAwesomeIcon
										icon={faServer}
										className='generalStyles__info generalStyles__mrFix'
									/>
									{t("edgeManagement.servers.servers")}
								</h5>
							</Divider>
							<div className={`drawer__box__${theme}`}>
								<div className='col-md-12'>
									<div className='generalStyles__flex'>
										<div className='generalStyles__inputFlexName'>
											<span className='generalStyles__inputFlexRequired'>
												*
											</span>
											<span className='drawer__text'>
												{t("edgeManagement.servers.servers")}
											</span>
										</div>
										<div className='generalStyles__width100'>
											<Select
												className='generalStyles__width100'
												disabled={listServers.length === 0}
												showSearch
												optionFilterProp='children'
												size='small'
												filterOption={(input: any, option: any) =>
													option.children
														.toLowerCase()
														.includes(input.toLowerCase())
												}
												value={server}
												onChange={onChangeServer}
											>
												{listServers.map((st) => (
													<Option key={st.id} value={st.id}>
														{st.name}
													</Option>
												))}
											</Select>
										</div>
										{listServers && listServers.length === 0 && (
											<div>
												<Tooltip
													title={t(
														"edgeManagement.servers.noServersFoundDescription"
													)}
													placement='top'
												>
													<FontAwesomeIcon
														icon={faCircleInfo}
														className='generalStyles__infoTooltipIcon'
													/>
												</Tooltip>
											</div>
										)}
									</div>
								</div>
							</div>
						</>
					) : (
						<>
							{/** ---------------------------------------------------- */}
							{/** SERVERS -- */}
							<br />
							<Divider
								orientation='left'
								className='generalStyles__drawerDivider'
							>
								<h5>
									<FontAwesomeIcon
										icon={faServer}
										className='generalStyles__info generalStyles__mrFix'
									/>
									{t("edgeManagement.servers.server")}
								</h5>
							</Divider>
							<div className={`drawer__box__${theme} row`}>
								{/** SERVER AUTO SELECTED */}
								<div className='col-md-12'>
									<div className='generalStyles__flex'>
										<div className='generalStyles__inputFlexName'>
											<span className='generalStyles__inputFlexRequired'>
												*
											</span>
											<span className='drawer__text'>
												{t("edgeManagement.servers.server")}
											</span>
										</div>
										<div className='generalStyles__width100'>
											<Input
												className=''
												size='small'
												disabled
												value={nameServerSelected}
											/>
										</div>
										<div>
											<Tooltip
												title={`${t(
													"tooltip.assignBankToServer1"
												)} ${nameServerSelected} ${t(
													"tooltip.assignBankToServer2"
												)}`}
												placement='top'
											>
												<FontAwesomeIcon
													icon={faCircleInfo}
													className='generalStyles__infoTooltipIcon'
												/>
											</Tooltip>
										</div>
									</div>
								</div>
							</div>
						</>
					)}
					<br />
					{/** ---------------------------------------------------- */}
					{/** CHANNELS -- */}
					<Divider orientation='left' className='generalStyles__drawerDivider'>
						<h5>
							<FontAwesomeIcon
								icon={faPlug}
								className='generalStyles__info generalStyles__mrFix'
							/>
							{t("edgeManagement.channels.channels")}
						</h5>
					</Divider>
					<div className={`drawer__box__${theme} row`}>
						{channels && channels.length > 0 ? (
							<div className='col-md-12'>
								<div className='generalStyles__flex'>
									<div className='generalStyles__inputFlexName'>
										<span className='generalStyles__inputFlexRequired'>*</span>
										<span className='drawer__text'>
											{t("edgeManagement.channels.channels")}
										</span>
									</div>
									<div className='generalStyles__width100'>
										<Select
											className='generalStyles__width100'
											disabled={channels.length === 0}
											showSearch
											optionFilterProp='children'
											size='small'
											filterOption={(input: any, option: any) =>
												option.children
													.toLowerCase()
													.includes(input.toLowerCase())
											}
											value={channel}
											onChange={onChangeChannel}
										>
											{channels.map((st: any) => (
												<Option key={st.id} value={st.id}>
													{st.name}
												</Option>
											))}
										</Select>
									</div>
								</div>
							</div>
						) : (
							<Alert
								message={t("edgeManagement.banks.noChannelsAssigned")}
								type='warning'
								showIcon
								closable
							/>
						)}
					</div>
				</div>
				{/** ---------------------------------------------------- */}
				{/** ASSIGN CONFIGURATION FILE -- */}
				<br />
				{(channelType === "SERIAL" || channelType === "TCP") &&
					channels &&
					channels.length > 0 && (
						<ConfigurationFilesAssignConfiguration
							setDataBlocks={setDataBlocks}
							liftType={liftType}
							setFloorsAndDirectionDAta={setFloorsAndDirectionDAta}
						/>
					)}
				{/** ---------------------------------------------------- */}
				{/** ASSIGN ADAPTER -- */}
				{channelType === "ADAPTER" && channels && channels.length > 0 && (
					<AdaptersAssignAdapter
						liftType={liftType}
						setFloorsAndDirectionDAta={setFloorsAndDirectionDAta}
					/>
				)}
				{/** ---------------------------------------------------- */}
				{/** NO CHANNEL ASSIGNED -- */}
				{channelType === "" && (
					<div>
						<Divider
							orientation='left'
							className='generalStyles__drawerDivider'
						>
							<h5>
								<FontAwesomeIcon
									icon={faGear}
									className='generalStyles__info generalStyles__mrFix'
								/>
								{t("edgeManagement.banks.configurationAndAdapters")}
							</h5>
						</Divider>
						<div className={`drawer__box__${theme}`}>
							<Alert
								message={t(
									"edgeManagement.banks.configurationAndAdaptersDescription"
								)}
								type='warning'
								showIcon
								closable
							/>
						</div>
					</div>
				)}
				{/** ---------------------------------------------------- */}
				{/** CLOSE AND SUBMIT BUTTONS */}
				<div className='mt-4'>
					<div>
						<Button
							type='default'
							onClick={onClickClose}
							className='buttonStyle__3'
						>
							{t("organizationManagement.close")}
						</Button>
						<Button
							onClick={onClickSave}
							disabled={
								isLoadingPostBank ||
								name === "" ||
								server === "" ||
								channel === ""
							}
							type='primary'
							className='buttonStyle__3'
						>
							{t("general.save")}
						</Button>
					</div>
				</div>
			</Spin>
		</Drawer>
	);
}

export default BanksCreateForm;
